import {generateAlignerOptions, safeParseInt} from 'utils/ConstFunctions'
import {
  DeliveryPreference,
  ManufacturingItem,
} from 'screens/Patients/LeadsProfile/main/overview/types/GettingStarted.types'
import {ITreatmentPlan} from 'screens/Patients/LeadsProfile/main/treatment/types/treatmentPlan.types'

export type EditableProductionBatch = ManufacturingItem & {
  id?: number
  batch_type?: DeliveryPreference
  instructions?: string | null
  case_type?: string | null
  product_name?: string | null
  product_description?: string | null
  product_image?: string | null
}

export const getProductionBatchId = (batch: {manufacturing_batch_id?: unknown; id?: unknown}) =>
  safeParseInt(batch.manufacturing_batch_id ?? batch.id)

export const canEditProductionBatch = (
  batch?: {
    status?: string | null
    delivered_on?: string | null
    is_already_delivered?: boolean
  } | null
) =>
  !!batch &&
  !!batch.status &&
  !['DELIVERED', 'CANCELLED'].includes(batch.status) &&
  !batch.delivered_on &&
  !batch.is_already_delivered

export const getEditableBatchAlignerOptions = (
  batch: EditableProductionBatch,
  batches: EditableProductionBatch[],
  plan: Partial<
    Pick<
      ITreatmentPlan,
      | 'upper_jaw_details'
      | 'lower_jaw_details'
      | 'aligner_details_meta_data'
      | 'aligner_treatment_details'
    >
  >
) => {
  const getJaw = (jaw: 'upper' | 'lower') => {
    const start = safeParseInt(batch[`${jaw}_aligner_start`])
    if (!start) return {start: null, end: null}

    const planRanges = [
      plan.aligner_details_meta_data?.[`${jaw}_jaw`],
      plan.aligner_treatment_details?.[`${jaw}_jaw`],
      plan[`${jaw}_jaw_details`],
    ]
    let end =
      planRanges
        .map((range) => safeParseInt(range?.ends_with))
        .find((planEnd) => planEnd >= start) || safeParseInt(batch[`${jaw}_aligner_end`])

    batches.forEach((other) => {
      if (getProductionBatchId(other) === getProductionBatchId(batch)) return
      if (other.status === 'CANCELLED') return
      const otherStart = safeParseInt(other[`${jaw}_aligner_start`])
      const otherEnd = safeParseInt(other[`${jaw}_aligner_end`])
      if (otherStart && otherEnd >= start) end = Math.min(end, otherStart - 1)
    })
    return {start, end}
  }

  const upper = getJaw('upper')
  const lower = getJaw('lower')
  return generateAlignerOptions(lower.start, lower.end, upper.start, upper.end)
}
